import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Modal, Button, Form } from "react-bootstrap";
import Header from "../layouts/Header";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";

const API = process.env.REACT_APP_API;

export default function AdminPositions() {
  const { setLoading } = useLoading();
  const [positions, setPositions] = useState([]);

  const [showForm, setShowForm]   = useState(false);
  const [editMode, setEditMode]   = useState(false);
  const [form, setForm]           = useState({ pos_id: '', pos_name: '' });
  const [formError, setFormError] = useState('');

  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`${API}/admin/getPositions`)
      .then(res => setPositions(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { load(); }, [load]);

  // ── Add ──
  const openAdd = () => {
    setForm({ pos_id: '', pos_name: '' });
    setFormError('');
    setEditMode(false);
    setShowForm(true);
  };

  // ── Edit ──
  const openEdit = (p) => {
    setForm({ pos_id: p.pos_id, pos_name: p.pos_name });
    setFormError('');
    setEditMode(true);
    setShowForm(true);
  };

  const handleFormSave = async () => {
    setFormError('');
    if (!form.pos_id || !form.pos_name) { setFormError('กรุณากรอกรหัสและชื่อสิทธิ์'); return; }
    if (!editMode && isNaN(Number(form.pos_id))) { setFormError('รหัสสิทธิ์ต้องเป็นตัวเลข'); return; }
    try {
      if (editMode) {
        await axiosInstance.post(`${API}/admin/updatePosition`, { pos_id: form.pos_id, pos_name: form.pos_name });
      } else {
        await axiosInstance.post(`${API}/admin/addPosition`, { pos_id: Number(form.pos_id), pos_name: form.pos_name });
      }
      setShowForm(false);
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  // ── Delete ──
  const openDelete = (p) => {
    setDeleteTarget(p);
    setDeleteError('');
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    setDeleteError('');
    try {
      await axiosInstance.post(`${API}/admin/deletePosition`, { pos_id: deleteTarget.pos_id });
      setShowConfirm(false);
      load();
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">จัดการประเภทสิทธิ์</h4>
        <Button size="sm" variant="primary" onClick={openAdd}>
          <i className="ri-add-line me-1"></i>เพิ่มสิทธิ์
        </Button>
      </div>

      <Card className="card-one">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 80 }}>รหัส</th>
                  <th>ชื่อสิทธิ์</th>
                  <th style={{ width: 130 }}></th>
                </tr>
              </thead>
              <tbody>
                {positions.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-secondary py-4">ไม่พบข้อมูล</td></tr>
                ) : positions.map(p => (
                  <tr key={p.pos_id}>
                    <td className="align-middle fw-semibold text-nowrap">{p.pos_id}</td>
                    <td className="align-middle">{p.pos_name}</td>
                    <td className="align-middle text-nowrap">
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(p)}>
                        <i className="ri-edit-line"></i>
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => openDelete(p)}>
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? 'แก้ไขสิทธิ์' : 'เพิ่มสิทธิ์ใหม่'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <div className="alert alert-danger py-2">{formError}</div>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>รหัสสิทธิ์ (pos_id) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                value={form.pos_id}
                onChange={e => setForm(f => ({ ...f, pos_id: e.target.value }))}
                disabled={editMode}
                placeholder="เช่น 20"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>ชื่อสิทธิ์ <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={form.pos_name}
                onChange={e => setForm(f => ({ ...f, pos_name: e.target.value }))}
                placeholder="เช่น ฝ่ายกิจการนักเรียน"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleFormSave}>บันทึก</Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Delete */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>ยืนยันการลบ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && <div className="alert alert-danger py-2">{deleteError}</div>}
          {deleteTarget && (
            <p>ต้องการลบสิทธิ์ <strong>{deleteTarget.pos_name}</strong> (#{deleteTarget.pos_id}) หรือไม่?</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>ยกเลิก</Button>
          <Button variant="danger" onClick={handleDelete}>ลบ</Button>
        </Modal.Footer>
      </Modal>
      </div>
    </React.Fragment>
  );
}
